/**
 * Shared declarative data table for tenant + admin.
 *
 * Replaces hand-rolled `<table>` + `theadClass`/`thClass`/`tdClass` patterns.
 * Single source of truth for sortable headers, row-click, action column,
 * mobile card fallback, empty state, sticky header, and toolbar slot.
 *
 * Sort can be controlled (parent owns state, e.g. for API sort params) or
 * uncontrolled (DataTable sorts in-memory using `column.accessor`).
 *
 * Optional, opt-in and fully controlled (the page owns the state):
 *  - `selection`  → a leading checkbox column, a tri-state header over the
 *    rows of the CURRENT page, and a "N selected · select all M" banner
 *    (derivation in `lib/selection.ts::selectionSummary`);
 *  - `expandable` → a chevron column that opens a full-width detail row.
 * Neither changes anything for a table that does not pass it. A table that
 * passes either mounts only the layout on screen (table OR cards, chosen by
 * `useIsMobile`, Standards §20.5), so each control and each detail exists
 * once; that needs the `ViewportProvider` both apps mount at their root.
 *
 * See `documentation/Standards.md` § "DataTable" for full API + examples.
 */
import React, { Fragment, useEffect, useId, useMemo, useState } from 'react';
import {
  tableCardClass,
  tableScrollClass,
  tableClass,
  theadClass,
  thClass,
  tbodyClass,
  trClass,
  tdClass,
} from '../lib/tableClasses';
import { bgLayer2 } from '../lib/layerClasses';
import {
  fillTemplate,
  selectionIntent,
  selectionMode,
  selectionSummary,
  toggleKey,
  triState,
  type SelectionIntent,
  type SelectionKey,
} from '../lib/selection';
import { domIdPart, withLeadingCells } from '../lib/dataTable';
import { useIsMobile } from '../contexts/ViewportContext';
import EmptyState from './EmptyState';
import { Skeleton } from './Skeleton';
import Pagination from './Pagination';
import type { PaginationLabels } from './Pagination';
import Checkbox from './Checkbox';
import Button from './Button';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type SortDir = 'asc' | 'desc';

export interface DataTableColumn<T> {
  /** Stable identifier — used for sort state + React keys. */
  key: string;
  /** Header label / node. Pass `null` for unlabelled columns (e.g. leading swatch / trailing badge). */
  header: React.ReactNode;
  /** Cell renderer for desktop. */
  render: (row: T) => React.ReactNode;
  /** When true, header becomes a clickable sort toggle. Requires `accessor` for uncontrolled sort. */
  sortable?: boolean;
  /** Value used for in-memory sorting (uncontrolled mode). Strings sort case-insensitive, numbers natural. */
  accessor?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  /** Extra Tailwind utilities merged into the `<td>`. */
  className?: string;
  /** Extra Tailwind utilities merged into the `<th>` (e.g. `w-12` for leading swatch column). */
  headerClassName?: string;
}

/** Copy for the selection column. Plain strings: the tenant passes `t('…')`
 *  (no variables — DataTable fills them), the admin passes Spanish literals. */
export interface DataTableSelectionLabels {
  /** Accessible name of each row checkbox («Seleccionar fila»). */
  selectRow: string;
  /** Accessible name of the header checkbox; also the visible text of the
   *  mobile «select page» strip («Seleccionar esta página»). */
  selectAllOnPage: string;
  /** Banner text, with `{count}` and `{total}` («{count} de {total} seleccionados»). */
  selectedOfMatching: string;
  /** Banner action, with `{total}` («Seleccionar los {total}»). */
  selectAllMatching: string;
  /** Banner action once everything is selected («Quitar selección»). */
  clear: string;
}

interface DataTableSelectionBase<T> {
  /** CONTROLLED: the keys (as returned by `rowKey`) that are selected. */
  selectedKeys: ReadonlySet<SelectionKey>;
  /** Receives the whole next set — never a delta. */
  onChange: (next: Set<SelectionKey>) => void;
  /** Rows that cannot be selected show no checkbox (see `renderUnselectable`)
   *  and never count towards the header's «all». */
  isRowSelectable?: (row: T) => boolean;
  /** What to draw in the checkbox cell of a row that cannot be selected
   *  (e.g. an «already in stock» tick). Default: nothing. */
  renderUnselectable?: (row: T) => React.ReactNode;
  /** Offer bulk selection: the header «select page» checkbox AND the «select
   *  all matching» banner. Default `true`. `false` hides both — a picker over a
   *  huge catalogue hides them until a filter narrows the list, and a banner
   *  offering «select all N» would undo that one click later. */
  showSelectAll?: boolean;
  labels: DataTableSelectionLabels;
}

/** In-memory list (the table holds every matching row, `paginated` or not):
 *  DataTable works out the matching set, «all matching» and the banner
 *  itself. Pass none of the server-paged props. */
interface DataTableSelectionInMemory {
  matchingCount?: undefined;
  allMatchingSelected?: undefined;
  selectedMatchingCount?: undefined;
  onSelectAllMatching?: undefined;
  onClearAllMatching?: undefined;
}

/**
 * Server-paged list: only the server knows the rows beyond this page, so «all
 * matching» is a FLAG the page owns (and sends to its bulk endpoint) — never
 * derived from `selectedKeys`, which holds loaded rows and keys of other
 * filters. While the flag is on, the visible page draws as selected; a hand
 * change (a row or the header) first calls `onClearAllMatching()` and then
 * `onChange` with the page made explicit minus that change — unticking one row
 * leaves the rest of the page ticked. A list that does not offer «select all
 * matching» passes none of this: then only the page is selectable.
 */
interface DataTableSelectionServer {
  /** The server's `total` of SELECTABLE rows matching the current filter. */
  matchingCount: number;
  /** The page's flag. */
  allMatchingSelected: boolean;
  /** Selected rows within the current filter, if the page knows it (banner
   *  `{count}`). Default: the selected rows of the current page. */
  selectedMatchingCount?: number;
  /** Turn the flag on (the banner's «Seleccionar los N»). */
  onSelectAllMatching: () => void;
  /** Turn the flag off AND drop what the page holds for this filter — the
   *  banner's «Quitar selección» (only the page knows those keys). */
  onClearAllMatching: () => void;
}

export type DataTableSelection<T> = DataTableSelectionBase<T> & (DataTableSelectionInMemory | DataTableSelectionServer);

export interface DataTableExpansion<T> {
  /** Detail rendered in a full-width row under the row (desktop) or inside
   *  the card (mobile) — mounted ONCE, in the layout on screen, so it may hold
   *  inputs with ids, state and effects. */
  renderExpanded: (row: T) => React.ReactNode;
  /** CONTROLLED: the keys (as returned by `rowKey`) that are open. */
  expandedKeys: ReadonlySet<SelectionKey>;
  onExpandedChange: (next: Set<SelectionKey>) => void;
  /** Rows without detail get no chevron. Default: every row. */
  isRowExpandable?: (row: T) => boolean;
  /** Accessible names of the chevron button. */
  labels: { expand: string; collapse: string };
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  /** Stable row key. */
  rowKey: (row: T) => string | number;

  /* ── Sorting ─────────────────────────────────────────── */
  /** Controlled sort — parent owns state (e.g. server-side sort). */
  sortKey?: string | null;
  sortDir?: SortDir;
  /** Initial sort for uncontrolled mode. Ignored when `sortKey` is provided. */
  defaultSortKey?: string;
  defaultSortDir?: SortDir;
  onSortChange?: (key: string, dir: SortDir) => void;

  /* ── Row interaction ─────────────────────────────────── */
  onRowClick?: (row: T) => void;
  /** Dynamic className per row (for status-based styling). */
  rowClassName?: (row: T) => string;
  /** Trailing right-aligned cell rendered after the last column. Receives a stop-propagation wrapper. */
  actions?: (row: T) => React.ReactNode;
  /** Content for the trailing actions column header (right-aligned). Defaults to
   *  empty. Use for a summary/count badge that should sit at the far right of the
   *  header row. Only rendered when `actions` is also provided. */
  actionsHeader?: React.ReactNode;
  /** Screen-reader name of the trailing actions column (it has no visible
   *  header). Spanish default; the tenant passes its `t()`. */
  actionsLabel?: string;
  /** Totals row. Receives the rows on the current page and returns a `<tr>`
   *  (with `<td>`s aligned to `columns` + the actions cell) rendered inside a
   *  `<tfoot>` — column-aligned, unlike the free-form `footer` slot. With
   *  `selection` / `expandable` there are 1–2 leading control columns: when
   *  the function returns a literal `<tr>` (or a fragment of `<tr>`s)
   *  DataTable prepends their empty cells itself; a COMPONENT that renders its
   *  own `<tr>` is opaque to it and must pad itself with `leadingCells` empty
   *  `<td>`s (the second argument; 0 without selection/expansion). */
  renderFooterRow?: (rows: T[], leadingCells: number) => React.ReactNode;

  /* ── Selection / expansion (optional, controlled) ────── */
  /** Leading checkbox column + tri-state header + «select all matching» banner. */
  selection?: DataTableSelection<T>;
  /** Chevron column that opens `renderExpanded(row)` under the row. */
  expandable?: DataTableExpansion<T>;

  /* ── Mobile fallback ─────────────────────────────────── */
  /** If provided, renders a `md:hidden` card list using this function instead of the table on small screens. */
  renderMobile?: (row: T) => React.ReactNode;
  /** When `renderMobile` is omitted and `mobileFallback` is `'auto'` (default), DataTable derives a simple list
   *  from columns. Pass `'scroll'` to skip the card layout and let the table scroll horizontally on mobile. */
  mobileFallback?: 'auto' | 'scroll';

  /* ── Grouping (optional) ─────────────────────────────── */
  /** Group rows under collapsible headers. Omit for a flat table — the other
   *  49 call sites are unaffected. */
  groupBy?: (row: T) => string | number;
  /** Header row for a group. Receives the key and that group's rows, so it can
   *  show aggregates (units, cost, an alert). Rendered as a full-width cell. */
  renderGroupHeader?: (key: string | number, rows: T[]) => React.ReactNode;
  /** Collapsed group keys. CONTROLLED: the page owns this, because the toolbar's
   *  collapse-all button shares the same state and persists it. */
  collapsedGroups?: Record<string, boolean>;
  /** Paginate by GROUP instead of by row, so a group is never split across two
   *  pages. Only meaningful with `groupBy` + `paginated`. */
  groupPagination?: boolean;

  /* ── Loading ─────────────────────────────────────────── */
  /** The rows are on their way (the load standard, Standards.md §21). With no
   *  rows yet it draws ghost rows shaped like the columns — never the empty
   *  state, which would ASSERT there is nothing. With rows already shown (a new
   *  page, filter or period) it keeps them, dimmed, until the new ones land. */
  loading?: boolean;
  /** Ghost rows on a first load. Default 6. */
  loadingRows?: number;
  /** For screen readers only. Default «Cargando». */
  loadingLabel?: string;

  /* ── Empty state ─────────────────────────────────────── */
  emptyIcon?: IconName;
  emptyLabel?: string;
  /** Full override — replaces the default `EmptyState`. */
  emptySlot?: React.ReactNode;

  /* ── Layout ──────────────────────────────────────────── */
  /** Slot rendered above the table inside the card (search + filters). */
  toolbar?: React.ReactNode;
  /** Slot rendered below the table inside the card (pagination, totals, etc.). */
  footer?: React.ReactNode;
  /** Sticky `<thead>` — table must live inside a scroll container with bounded height. */
  stickyHeader?: boolean;
  /** Override the header strip's background. The default is layer 2, which
   *  disappears when the table is dropped onto a layer-2 card (the piece cost
   *  table sits on one) — same tone on both sides, so the header strip reads as
   *  no header at all. */
  theadClassName?: string;
  className?: string;

  /* ── Pagination (client-side) ─────────────────────────── */
  /** Enable built-in client-side pagination: DataTable sorts the FULL data set
   *  (global sort), slices to the current page, and renders the standard
   *  `Pagination` bar *below* the card. Gives correct cross-page sort for free. */
  paginated?: boolean;
  /** Page-size options (default 20/50/200). */
  pageSizeOptions?: number[];
  /** Initial page size (default 20). */
  initialPageSize?: number;
  /** Reset to page 1 when this changes (e.g. the search/filter key). */
  paginationResetKey?: unknown;
  /** Bar labels — required when `paginated` (admin passes Spanish literals;
   *  tenant passes `usePaginationLabels()`). */
  paginationLabels?: PaginationLabels;
}

const alignClass: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/** Width of the leading control columns (checkbox / chevron). */
const controlThClass = `${thClass} w-10 !pr-0`;
const controlTdClass = `${tdClass} w-10 !pr-0 align-middle`;
const selectedRowClass = 'bg-primary/5 dark:bg-primary/10';

/**
 * Shared sort comparator. Exported so callers that paginate *before* handing
 * rows to DataTable (controlled-sort mode) can sort the full dataset with the
 * exact same semantics the table would have applied to a single page.
 */
export function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

interface SortIndicatorProps {
  active: boolean;
  dir: SortDir;
}

function SortIndicator({ active, dir }: SortIndicatorProps) {
  const icon: IconName = !active ? 'unfold_more' : dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  const color = active
    ? 'text-primary dark:text-primary'
    : 'text-slate-300 dark:text-slate-600 group-hover/sort:text-slate-400 dark:group-hover/sort:text-slate-500';
  return (
    <Icon name={icon} className={`!text-[14px] !leading-none transition-colors ${color}`} />
  );
}

interface SelectBoxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  /** Accessible name — sr-only unless `showLabel`. */
  label: string;
  showLabel?: boolean;
  onToggle: () => void;
}

/**
 * The shared round `Checkbox`, wrapped in our own `<label>` so the hidden input
 * gets an accessible name (the bare circle has none) and so the click never
 * reaches a clickable row: the label click AND the synthetic input click both
 * bubble through here, and both are stopped.
 */
function SelectBox({ checked, indeterminate = false, disabled = false, label, showLabel = false, onToggle }: SelectBoxProps) {
  return (
    <label
      className={`inline-flex items-center gap-2 ${showLabel ? 'text-xs text-slate-600 dark:text-slate-400 select-none' : 'p-1 -m-1'} ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      }`}
      title={showLabel ? undefined : label}
      onClick={e => e.stopPropagation()}
    >
      <Checkbox bare checked={checked} indeterminate={indeterminate} disabled={disabled} onChange={() => onToggle()} />
      <span className={showLabel ? '' : 'sr-only'}>{label}</span>
    </label>
  );
}

interface ExpandToggleProps {
  open: boolean;
  controls: string;
  labels: { expand: string; collapse: string };
  onToggle: () => void;
}

/** Chevron button: a real `<button>` (Enter/Space for free), `aria-expanded`
 *  + `aria-controls` pointing at the detail it opens — only while it is open,
 *  so the IDREF never names an element that does not exist. */
function ExpandToggle({ open, controls, labels, onToggle }: ExpandToggleProps) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      aria-label={open ? labels.collapse : labels.expand}
      title={open ? labels.collapse : labels.expand}
      onClick={e => {
        e.stopPropagation();
        onToggle();
      }}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-surface-hover-dark dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      <Icon name="chevron_right" className={`!text-[20px] transition-transform ${open ? 'rotate-90' : ''}`} />
    </button>
  );
}

/** Narrowing for the selection union; the decision itself is `selectionMode`
 *  (lib/selection.ts, pinned by its test). */
function isServerSelection<T>(
  selection: DataTableSelection<T>,
): selection is DataTableSelectionBase<T> & DataTableSelectionServer {
  return selectionMode(selection) === 'server';
}

/**
 * Renders ONE of the two layouts, chosen by `useIsMobile` (Standards §20.5).
 * A table with `selection` or `expandable` goes through here: its rows carry
 * controls with handlers and an expanded detail is arbitrary caller content
 * (inputs with ids, effects, fetches, its own state), so the old «draw both and
 * hide one with CSS» would mount every detail twice. The callbacks build only
 * the layout that is shown. Mounted only by such tables, so the 49 plain ones
 * keep their CSS layouts and never need the provider.
 */
function LayoutSwitch({ desktop, mobile }: { desktop: () => React.ReactNode; mobile: () => React.ReactNode }) {
  const isMobile = useIsMobile();
  return <>{isMobile ? mobile() : desktop()}</>;
}

export default function DataTable<T>({
  data,
  columns,
  rowKey,
  sortKey: sortKeyProp,
  sortDir: sortDirProp,
  defaultSortKey,
  defaultSortDir = 'asc',
  onSortChange,
  onRowClick,
  rowClassName,
  actions,
  actionsHeader,
  actionsLabel = 'Acciones',
  renderFooterRow,
  selection,
  expandable,
  renderMobile,
  mobileFallback = 'auto',
  groupBy,
  renderGroupHeader,
  collapsedGroups,
  groupPagination = false,
  loading = false,
  loadingRows = 6,
  loadingLabel = 'Cargando',
  emptyIcon = 'inbox',
  emptyLabel = '—',
  emptySlot,
  toolbar,
  footer,
  stickyHeader = false,
  theadClassName,
  className = '',
  paginated = false,
  pageSizeOptions,
  initialPageSize = 20,
  paginationResetKey,
  paginationLabels,
}: DataTableProps<T>) {
  const isControlled = sortKeyProp !== undefined;
  const [internalSort, setInternalSort] = useState<{ key: string | null; dir: SortDir }>({
    key: defaultSortKey ?? null,
    dir: defaultSortDir,
  });
  const baseId = useId();

  const sortKey = isControlled ? sortKeyProp : internalSort.key;
  const sortDir: SortDir = (isControlled ? sortDirProp : internalSort.dir) ?? 'asc';

  const toggleSort = (key: string) => {
    const nextDir: SortDir = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    if (isControlled) {
      onSortChange?.(key, nextDir);
    } else {
      setInternalSort({ key, dir: nextDir });
      onSortChange?.(key, nextDir);
    }
  };

  const sortedData = useMemo(() => {
    if (isControlled || !sortKey) return data;
    const col = columns.find(c => c.key === sortKey);
    if (!col?.accessor) return data;
    const accessor = col.accessor;
    const out = [...data].sort((a, b) => compareValues(accessor(a), accessor(b)));
    return sortDir === 'desc' ? out.reverse() : out;
  }, [data, columns, sortKey, sortDir, isControlled]);

  // Grouping: preserve first-appearance order of the groups and the sorted
  // order of the rows inside each. Built before pagination so a page can be a
  // slice of GROUPS rather than of rows.
  const groups = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map<string | number, T[]>();
    for (const row of sortedData) {
      const key = groupBy(row);
      const bucket = map.get(key);
      if (bucket) bucket.push(row);
      else map.set(key, [row]);
    }
    return Array.from(map, ([key, rows]) => ({ key, rows }));
  }, [sortedData, groupBy]);

  const groupedPagination = !!groups && groupPagination;
  const totalRows = groupedPagination ? groups.length : sortedData.length;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  // Back to page 1 when the page size, the explicit reset key, or the row count
  // changes (a filter that narrows the set). Sorting (same count) keeps the page.
  useEffect(() => {
    setPage(1);
  }, [paginationResetKey, pageSize, totalRows]);

  const totalPages = paginated ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  // Slice AFTER sorting → the current page is the right slice of the globally
  // sorted set, so column sort is stable across pages.
  const pageGroups = groups
    ? (paginated && groupedPagination
        ? groups.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : groups)
    : null;
  const pageData = pageGroups
    ? pageGroups.flatMap(g => g.rows)
    : paginated
      ? sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : sortedData;

  const isCollapsed = (key: string | number) => !!collapsedGroups?.[String(key)];

  const isEmpty = sortedData.length === 0;

  /* ── Selection ─────────────────────────────────────────────────────── */
  // All the derivation (header tri-state, «all matching», banner, its count)
  // is `selectionSummary` in lib/selection.ts, pinned by a Node test; this
  // block only feeds it and renders what it says.
  //
  // The header is tri-state over the rows the user can SEE on this page: a
  // collapsed group's rows are on the page but hidden, and ticking things you
  // cannot see is how a bulk action surprises someone.
  const isSelectable = (row: T) => !selection?.isRowSelectable || selection.isRowSelectable(row);
  const visibleRows = pageGroups
    ? pageGroups.flatMap(g => (isCollapsed(g.key) ? [] : g.rows))
    : pageData;
  const pageSelectableKeys = selection ? visibleRows.filter(isSelectable).map(rowKey) : [];
  const server = selection && isServerSelection(selection) ? selection : null;
  // In memory (no `matchingCount`): every selectable row of `data` matches.
  const inMemoryMatchingKeys = selection && !server ? sortedData.filter(isSelectable).map(rowKey) : null;
  const showSelectAll = !!selection && selection.showSelectAll !== false;
  const summary = selection
    ? selectionSummary({
        pageSelectableKeys,
        selectedKeys: selection.selectedKeys,
        matchingKeys: inMemoryMatchingKeys,
        server: server
          ? {
              matchingCount: server.matchingCount,
              allMatchingSelected: server.allMatchingSelected,
              selectedMatchingCount: server.selectedMatchingCount,
            }
          : null,
        showSelectAll,
      })
    : null;
  const pageState = summary?.pageState ?? 'none';
  const banner = summary?.banner ?? 'hidden';
  // What the rows DRAW: on a server-paged list with «all matching» on, the
  // visible page counts as selected although its keys need not be in the set.
  const effective: ReadonlySet<SelectionKey> = summary?.effective ?? new Set<SelectionKey>();
  // Every gesture goes through `selectionIntent` (lib/selection.ts, pinned by
  // its test), which decides the callbacks; this only fires them, in the
  // documented order: drop the server flag, raise it, then the explicit set.
  const run = (intent: SelectionIntent) => {
    if (!selection) return;
    const fx = selectionIntent(
      {
        mode: server ? 'server' : 'memory',
        selectedKeys: selection.selectedKeys,
        effective,
        matchingKeys: inMemoryMatchingKeys,
        allMatchingSelected: server?.allMatchingSelected ?? false,
      },
      intent,
    );
    if (fx.clearAllMatching) server?.onClearAllMatching();
    if (fx.selectAllMatching) server?.onSelectAllMatching();
    if (fx.next) selection.onChange(fx.next);
  };
  const headerCheckbox = selection && showSelectAll ? (
    <SelectBox
      checked={pageState === 'all'}
      indeterminate={pageState === 'some'}
      disabled={pageSelectableKeys.length === 0}
      label={selection.labels.selectAllOnPage}
      onToggle={() => run({ kind: 'page', keys: pageSelectableKeys })}
    />
  ) : null;
  const rowSelectCell = (row: T) => {
    if (!selection) return null;
    const key = rowKey(row);
    if (!isSelectable(row)) return selection.renderUnselectable?.(row) ?? null;
    return (
      <SelectBox
        checked={effective.has(key)}
        label={selection.labels.selectRow}
        onToggle={() => run({ kind: 'row', key })}
      />
    );
  };
  const isRowSelected = (row: T) => !!selection && isSelectable(row) && effective.has(rowKey(row));
  // The mobile «select this page» strip covers exactly the cards the mobile
  // list shows: with `renderMobile` that is the visible rows (collapsed groups
  // hidden, as on desktop); the derived fallback has no group headers and
  // shows every row of the page, so the strip covers all of them.
  const mobilePageKeys = selection
    ? (renderMobile ? pageSelectableKeys : pageData.filter(isSelectable).map(rowKey))
    : [];
  const mobilePageState = triState(mobilePageKeys, effective);

  /* ── Expansion ─────────────────────────────────────────────────────── */
  const isExpandable = (row: T) => !!expandable && (!expandable.isRowExpandable || expandable.isRowExpandable(row));
  const isExpanded = (row: T) => isExpandable(row) && !!expandable?.expandedKeys.has(rowKey(row));
  // ONE detail per open row exists (only the active layout is mounted, see
  // `LayoutSwitch`), so one id. Row keys come from workshop data and may hold
  // spaces, which would split the `aria-controls` IDREF — `domIdPart` escapes.
  const detailId = (row: T) => `${baseId}-detail-${domIdPart(rowKey(row))}`;
  const expandToggle = (row: T) =>
    expandable && isExpandable(row) ? (
      <ExpandToggle
        open={isExpanded(row)}
        controls={detailId(row)}
        labels={expandable.labels}
        onToggle={() => expandable.onExpandedChange(toggleKey(expandable.expandedKeys, rowKey(row)))}
      />
    ) : null;

  const leadingCols = (selection ? 1 : 0) + (expandable ? 1 : 0);
  const totalCols = leadingCols + columns.length + (actions ? 1 : 0);

  // A clickable row is reachable by keyboard too: Tab lands on it, Enter or
  // Space opens it. The <tr> keeps its table semantics (no role="button").
  const rowKeyHandler = (row: T) => (e: React.KeyboardEvent) => {
    if (!onRowClick || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    }
  };
  const mobileRowProps = (row: T) =>
    onRowClick ? { role: 'button' as const, tabIndex: 0, onKeyDown: rowKeyHandler(row) } : {};

  const renderRow = (row: T) => {
    const extra = rowClassName?.(row) ?? '';
    const clickable = onRowClick ? 'cursor-pointer' : '';
    const selected = isRowSelected(row) ? selectedRowClass : '';
    const open = isExpanded(row);
    return (
      <Fragment key={rowKey(row)}>
        <tr
          className={`${trClass} ${clickable} ${selected} ${extra}`.trim()}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onKeyDown={onRowClick ? rowKeyHandler(row) : undefined}
        >
          {selection && (
            <td className={controlTdClass} onClick={e => e.stopPropagation()}>
              {rowSelectCell(row)}
            </td>
          )}
          {/* The whole control cell swallows the click, not just the 28px
              button: a miss in its padding must not open the row. */}
          {expandable && (
            <td className={controlTdClass} onClick={e => e.stopPropagation()}>
              {expandToggle(row)}
            </td>
          )}
          {columns.map(col => {
            const align = col.align ?? 'left';
            return (
              <td key={col.key} className={`${tdClass} ${alignClass[align]} ${col.className ?? ''}`.trim()}>
                {col.render(row)}
              </td>
            );
          })}
          {actions && (
            <td className={`${tdClass} text-right`} onClick={e => e.stopPropagation()}>
              {actions(row)}
            </td>
          )}
        </tr>
        {open && expandable && (
          <tr id={detailId(row)} className={bgLayer2}>
            <td colSpan={totalCols} className="px-4 py-3">
              {expandable.renderExpanded(row)}
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  // Mobile card. The checkbox sits at the card's top-left and the chevron at
  // its top-right, around whatever the card renders; an open detail goes inside
  // the same card, under its content — one tap target per row, never a second
  // card that could be mistaken for another row.
  const wrapMobile = (row: T, content: React.ReactNode, contentClassName = 'min-w-0 flex-1') => {
    if (!selection && !expandable) return content;
    const open = isExpanded(row);
    return (
      <>
        <div className="flex items-start gap-3">
          {selection && (
            <div className="shrink-0 pt-0.5" onClick={e => e.stopPropagation()}>
              {rowSelectCell(row)}
            </div>
          )}
          <div className={contentClassName}>{content}</div>
          {expandable && (
            <div className="shrink-0 -my-1" onClick={e => e.stopPropagation()}>
              {expandToggle(row)}
            </div>
          )}
        </div>
        {open && expandable && (
          <div
            id={detailId(row)}
            className={`mt-3 rounded-lg p-3 ${bgLayer2}`}
            onClick={e => e.stopPropagation()}
          >
            {expandable.renderExpanded(row)}
          </div>
        )}
      </>
    );
  };

  // A table without selection/expansion keeps the exact card markup it always
  // had (byte for byte); the controls only add to it.
  const plainCards = !selection && !expandable;
  const clickableCard = onRowClick ? 'cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/30' : '';

  const renderMobileCard = (row: T) => (
    <div
      key={rowKey(row)}
      className={isRowSelected(row) ? `p-4 ${clickableCard} ${selectedRowClass}` : `p-4 ${clickableCard}`}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      {...mobileRowProps(row)}
    >
      {wrapMobile(row, renderMobile ? renderMobile(row) : null)}
    </div>
  );

  const hasMobileCards = !!renderMobile || mobileFallback === 'auto';
  // A table with `selection` or `expandable` mounts only the layout on screen
  // (`LayoutSwitch`, `useIsMobile`): its rows carry controls and its details
  // are caller content that must exist once. A plain table keeps the CSS
  // layouts it always had (both in the DOM, one hidden), unchanged.
  const switchLayouts = (!!selection || !!expandable) && hasMobileCards;
  // `overflow-hidden` makes the card a scroll container that never scrolls,
  // which pins any `sticky` child (the `BulkActionBar` in the toolbar slot)
  // in place. `overflow-clip` clips the rounded corners just the same without
  // being a scroll container, so the bar sticks to the page / modal body while
  // the card is on screen. Only for selectable tables — the others keep the
  // exact card they always had.
  const cardClass = selection ? tableCardClass.replace(/\boverflow-hidden\b/, 'overflow-clip') : tableCardClass;

  // Desktop table.
  const renderDesktop = () => (
    <div className={`${hasMobileCards ? 'hidden md:block' : ''} ${tableScrollClass}`}>
      <table className={tableClass}>
        <thead className={`${theadClassName ?? theadClass} ${stickyHeader ? 'sticky top-0 z-[1]' : ''}`.trim()}>
          <tr>
            {selection && (
              <th scope="col" className={controlThClass}>
                {headerCheckbox ?? <span className="sr-only">{selection.labels.selectRow}</span>}
              </th>
            )}
            {expandable && (
              <th scope="col" className={controlThClass}>
                <span className="sr-only">{expandable.labels.expand}</span>
              </th>
            )}
            {columns.map(col => {
              const align = col.align ?? 'left';
              const baseTh = `${thClass} ${alignClass[align]} ${col.headerClassName ?? ''}`.trim();
              if (!col.sortable) {
                return <th key={col.key} scope="col" className={baseTh}>{col.header}</th>;
              }
              const isActive = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={baseTh}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className={`group/sort inline-flex items-center gap-1.5 select-none transition-colors hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus-visible:text-slate-700 dark:focus-visible:text-slate-200 ${
                      isActive ? 'text-slate-700 dark:text-slate-200' : ''
                    } ${align === 'right' ? 'flex-row-reverse' : ''}`}
                  >
                    <span>{col.header}</span>
                    <SortIndicator active={isActive} dir={sortDir} />
                  </button>
                </th>
              );
            })}
            {actions && (
              <th scope="col" className={`${thClass} text-right`}>
                {actionsHeader ?? <span className="sr-only">{actionsLabel}</span>}
              </th>
            )}
          </tr>
        </thead>
        <tbody className={tbodyClass}>
          {pageGroups
            ? pageGroups.map(g => (
                <Fragment key={g.key}>
                  {renderGroupHeader && (
                    <tr className="bg-surface-card-light dark:bg-surface-hover-dark">
                      <td className={tdClass} colSpan={totalCols}>
                        {renderGroupHeader(g.key, g.rows)}
                      </td>
                    </tr>
                  )}
                  {!isCollapsed(g.key) && g.rows.map(renderRow)}
                </Fragment>
              ))
            : pageData.map(renderRow)}
        </tbody>
        {renderFooterRow && (
          <tfoot className="border-t border-border-subtle dark:border-border-subtle-dark font-semibold">
            {withLeadingCells(
              renderFooterRow(pageData, leadingCols),
              leadingCols,
              i => <td key={`__lead${i}`} className={controlTdClass} />,
              React,
            )}
          </tfoot>
        )}
      </table>
    </div>
  );

  // Mobile: the header checkbox lives in the table head, which the card list
  // does not have, so the cards get their own «select this page» strip.
  const renderMobileStrip = () =>
    hasMobileCards && headerCheckbox && selection ? (
      <div className={`md:hidden flex items-center px-4 py-2.5 border-b border-border-subtle dark:border-border-subtle-dark ${bgLayer2}`}>
        <SelectBox
          checked={mobilePageState === 'all'}
          indeterminate={mobilePageState === 'some'}
          disabled={mobilePageKeys.length === 0}
          label={selection.labels.selectAllOnPage}
          showLabel
          onToggle={() => run({ kind: 'page', keys: mobilePageKeys })}
        />
      </div>
    ) : null;

  const renderMobileList = () => (
    <>
      {/* Mobile card list */}
      {renderMobile && pageGroups && (
        <div className="md:hidden divide-y divide-border-subtle dark:divide-border-subtle-dark">
          {pageGroups.map(g => (
            <Fragment key={g.key}>
              {renderGroupHeader && (
                <div className="bg-surface-card-light dark:bg-surface-hover-dark px-4 py-2.5">
                  {renderGroupHeader(g.key, g.rows)}
                </div>
              )}
              {!isCollapsed(g.key) && g.rows.map(renderMobileCard)}
            </Fragment>
          ))}
        </div>
      )}

      {renderMobile && !pageGroups && (
        <div className="md:hidden divide-y divide-border-subtle dark:divide-border-subtle-dark">
          {pageData.map(renderMobileCard)}
        </div>
      )}

      {/* Auto mobile fallback: derive a simple list from columns */}
      {!renderMobile && mobileFallback === 'auto' && (
        <div className="md:hidden divide-y divide-border-subtle dark:divide-border-subtle-dark">
          {pageData.map(row => {
            const extra = rowClassName?.(row) ?? '';
            const derivedParts = (
              <>
                <div className="min-w-0 flex-1 space-y-1">
                  {columns.map((col, i) => (
                    <div
                      key={col.key}
                      className={
                        i === 0
                          ? 'text-sm font-medium text-slate-700 dark:text-slate-200 truncate'
                          : 'text-xs text-slate-500 dark:text-slate-400 truncate'
                      }
                    >
                      {col.render(row)}
                    </div>
                  ))}
                </div>
                {actions && (
                  <div onClick={e => e.stopPropagation()} className="shrink-0">
                    {actions(row)}
                  </div>
                )}
              </>
            );
            if (plainCards) {
              return (
                <div
                  key={rowKey(row)}
                  className={`p-4 flex items-start justify-between gap-3 ${clickableCard} ${extra}`.trim()}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  {...mobileRowProps(row)}
                >
                  {derivedParts}
                </div>
              );
            }
            return (
              <div
                key={rowKey(row)}
                className={`p-4 ${clickableCard} ${isRowSelected(row) ? selectedRowClass : ''} ${extra}`.trim()}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                {...mobileRowProps(row)}
              >
                {wrapMobile(row, <div className="flex items-start justify-between gap-3">{derivedParts}</div>)}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <>
    <div className={`${cardClass} ${className}`.trim()}>
      {toolbar}

      {isEmpty && loading && (
        <div role="status" aria-live="polite" className="flex flex-col">
          <span className="sr-only">{loadingLabel}</span>
          {Array.from({ length: loadingRows }).map((_, i) => (
            <div
              key={i}
              aria-hidden
              className={`flex items-center gap-4 px-4 py-3.5 ${i > 0 ? 'border-t border-border-subtle dark:border-border-subtle-dark' : ''}`}
            >
              {columns.map((col, c) => (
                <div
                  key={col.key}
                  className={`flex min-w-0 flex-1 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''} ${c === 0 ? 'flex-[2]' : ''} ${c > 2 ? 'max-md:hidden' : ''}`}
                >
                  {c === 0 ? (
                    <div className="flex w-full flex-col gap-1.5">
                      <Skeleton className="h-3 w-3/5" />
                      <Skeleton className="h-2.5 w-2/5" />
                    </div>
                  ) : (
                    <Skeleton className={`h-3 ${col.align === 'right' ? 'w-16' : 'w-3/5'}`} />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {isEmpty && !loading && (
        emptySlot ?? <EmptyState icon={emptyIcon} label={emptyLabel} />
      )}

      {!isEmpty && selection && summary && banner !== 'hidden' && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-border-subtle dark:border-border-subtle-dark bg-primary/5 dark:bg-primary/10 px-4 py-2 text-sm text-text-main dark:text-slate-200"
        >
          <span className="tabular-nums">
            {fillTemplate(selection.labels.selectedOfMatching, { count: summary.bannerCount, total: summary.matchingCount })}
          </span>
          {banner === 'offer' ? (
            <Button variant="ghost" size="sm" onClick={() => run({ kind: 'selectAllMatching' })}>
              {fillTemplate(selection.labels.selectAllMatching, { total: summary.matchingCount })}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => run({ kind: 'clearAllMatching' })}>
              {selection.labels.clear}
            </Button>
          )}
        </div>
      )}

      {!isEmpty && (
      <div aria-busy={loading || undefined} className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
      {switchLayouts ? (
        <LayoutSwitch
          desktop={renderDesktop}
          mobile={() => (
            <>
              {renderMobileStrip()}
              {renderMobileList()}
            </>
          )}
        />
      ) : (
        <>
          {renderDesktop()}
          {renderMobileStrip()}
          {renderMobileList()}
        </>
      )}
      </div>
      )}

      {footer}
    </div>
    {paginated && paginationLabels && totalRows > 0 && (
      <Pagination
        page={currentPage}
        pageSize={pageSize}
        totalItems={totalRows}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={pageSizeOptions}
        labels={paginationLabels}
      />
    )}
    </>
  );
}
